import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import Skills from './components/Skills';
import Projects from './components/Projects';
import Certifications from './components/Certifications';
import Education from './components/Education';
import Contact from './components/Contact';
import Footer from './components/Footer';

const AppContainer = styled.div`
  min-height: 100vh;
  background: #080b10;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      linear-gradient(rgba(255, 255, 255, 0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.025) 1px, transparent 1px),
      radial-gradient(circle at 12% 18%, rgba(73, 204, 255, 0.12) 0, transparent 26%),
      radial-gradient(circle at 86% 12%, rgba(87, 232, 180, 0.08) 0, transparent 28%),
      linear-gradient(135deg, #080b10 0%, #101722 45%, #081115 100%);
    background-size: 48px 48px, 48px 48px, auto, auto, auto;
    z-index: 0;
    pointer-events: none;
  }
`;

const MainContent = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;

  @media (max-width: 768px) {
    padding: 0 18px;
  }
`;

const MouseGlow = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(103, 216, 255, 0.14) 0%,
    rgba(122, 255, 200, 0.07) 34%,
    transparent 70%
  );
  filter: blur(8px);
  mix-blend-mode: screen;
  opacity: 0;
  pointer-events: none;
  z-index: 0;
  will-change: transform, opacity;
  transition: opacity 0.35s ease;

  @media (hover: none), (pointer: coarse), (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

function App() {
  const glowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const glow = glowRef.current;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const coarsePointer = window.matchMedia('(pointer: coarse)').matches;

    if (!glow || reduceMotion || coarsePointer) return;

    const position = {
      currentX: window.innerWidth / 2,
      currentY: window.innerHeight / 2,
      targetX: window.innerWidth / 2,
      targetY: window.innerHeight / 2
    };
    let animationFrame = 0;

    const animate = () => {
      position.currentX += (position.targetX - position.currentX) * 0.12;
      position.currentY += (position.targetY - position.currentY) * 0.12;
      glow.style.transform = `translate3d(${position.currentX - 150}px, ${position.currentY - 150}px, 0)`;
      animationFrame = requestAnimationFrame(animate);
    };

    const handlePointerMove = (event: PointerEvent) => {
      position.targetX = event.clientX;
      position.targetY = event.clientY;
      glow.style.opacity = '1';
    };

    const handlePointerLeave = () => {
      glow.style.opacity = '0';
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    document.documentElement.addEventListener('mouseleave', handlePointerLeave);
    animationFrame = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      document.documentElement.removeEventListener('mouseleave', handlePointerLeave);
      cancelAnimationFrame(animationFrame);
    };
  }, []);

  return (
    <AppContainer>
      <MouseGlow ref={glowRef} aria-hidden="true" />
      <Header />
      <MainContent style={{ position: 'relative', zIndex: 1 }}>
        <Hero />
        <About />
        <Skills />
        <Projects />
        <Certifications />
        <Education />
        <Contact />
      </MainContent>
      <Footer />
    </AppContainer>
  );
}

export default App; 
